export interface ThemeConfig {
  id: string;
  name: string;
  emoji: string;

  // Terminology
  kidRole: string;
  parentRole: string;
  coParentRole: string;
  superParentRole: string;
  challengeName: string;
  challengeNamePlural: string;
  pointName: string;
  pointNamePlural: string;
  pointEmoji: string;
  difficulty: {
    common: string;
    rare: string;
    epic: string;
    legendary: string;
  };
  challengeLogTitle: string;
  homeTitle: string;
  familyHubTitle: string;
  pointBankTitle: string;

  // Visual identity — values used as CSS custom property values
  visual: {
    bgFrom: string;
    bgTo: string;
    accent: string;
    accentGlow: string;
    surface: string;
  };
}

export const THEMES: Record<string, ThemeConfig> = {
  base: {
    id: "base",
    name: "Base",
    emoji: "🌟",
    kidRole: "Kid",
    parentRole: "Parent",
    coParentRole: "Parent",
    superParentRole: "Head Parent",
    challengeName: "Challenge",
    challengeNamePlural: "Challenges",
    pointName: "Point",
    pointNamePlural: "Points",
    pointEmoji: "💡",
    difficulty: { common: "Easy", rare: "Medium", epic: "Hard", legendary: "Legendary" },
    challengeLogTitle: "Activity Log",
    homeTitle: "Your Home",
    familyHubTitle: "Family Hub",
    pointBankTitle: "Points Bank",
    visual: {
      bgFrom: "#2a1f5c",
      bgTo: "#05050f",
      accent: "#ffd700",
      accentGlow: "rgba(255,215,0,0.3)",
      surface: "rgba(255,255,255,0.04)",
    },
  },
  explorer: {
    id: "explorer",
    name: "Explorer",
    emoji: "🗺️",
    kidRole: "Explorer",
    parentRole: "Quest Master",
    coParentRole: "Quest Master",
    superParentRole: "Head Quest Master",
    challengeName: "Quest",
    challengeNamePlural: "Quests",
    pointName: "Star",
    pointNamePlural: "Stars",
    pointEmoji: "⭐",
    difficulty: { common: "Easy", rare: "Medium", epic: "Hard", legendary: "Boss Level" },
    challengeLogTitle: "Quest Log",
    homeTitle: "Your House",
    familyHubTitle: "Family Hub",
    pointBankTitle: "Star Bank",
    visual: {
      bgFrom: "#0d2818",
      bgTo: "#051008",
      accent: "#4ade80",
      accentGlow: "rgba(74,222,128,0.3)",
      surface: "rgba(74,222,128,0.06)",
    },
  },
  wizard: {
    id: "wizard",
    name: "Wizard",
    emoji: "🧙",
    kidRole: "Apprentice",
    parentRole: "Grand Wizard",
    coParentRole: "Grand Wizard",
    superParentRole: "Arch Wizard",
    challengeName: "Spell",
    challengeNamePlural: "Spells",
    pointName: "Orb",
    pointNamePlural: "Orbs",
    pointEmoji: "🔮",
    difficulty: { common: "Simple Spell", rare: "Complex Spell", epic: "Ancient Spell", legendary: "Grand Spell" },
    challengeLogTitle: "Spell Book",
    homeTitle: "Your Tower",
    familyHubTitle: "Family Hub",
    pointBankTitle: "Orb Vault",
    visual: {
      bgFrom: "#1a0533",
      bgTo: "#05010f",
      accent: "#a855f7",
      accentGlow: "rgba(168,85,247,0.3)",
      surface: "rgba(168,85,247,0.06)",
    },
  },
  sports: {
    id: "sports",
    name: "Sports",
    emoji: "🏆",
    kidRole: "Player",
    parentRole: "Coach",
    coParentRole: "Coach",
    superParentRole: "Head Coach",
    challengeName: "Drill",
    challengeNamePlural: "Drills",
    pointName: "Trophy",
    pointNamePlural: "Trophies",
    pointEmoji: "🏆",
    difficulty: { common: "Warm-Up", rare: "Training", epic: "Championship", legendary: "World Cup" },
    challengeLogTitle: "Playbook",
    homeTitle: "Your Stadium",
    familyHubTitle: "Family Hub",
    pointBankTitle: "Trophy Cabinet",
    visual: {
      bgFrom: "#0a1628",
      bgTo: "#020b18",
      accent: "#f97316",
      accentGlow: "rgba(249,115,22,0.3)",
      surface: "rgba(249,115,22,0.06)",
    },
  },

  starwars: {
    id: "starwars",
    name: "Star Wars",
    emoji: "⚔️",
    kidRole: "Padawan",
    parentRole: "Jedi Master",
    coParentRole: "Jedi Master",
    superParentRole: "Grand Master",
    challengeName: "Mission",
    challengeNamePlural: "Missions",
    pointName: "Credit",
    pointNamePlural: "Credits",
    pointEmoji: "💫",
    difficulty: { common: "Youngling", rare: "Padawan", epic: "Knight", legendary: "Master" },
    challengeLogTitle: "Mission Log",
    homeTitle: "Your Base",
    familyHubTitle: "Jedi Council",
    pointBankTitle: "Credit Vault",
    visual: {
      bgFrom: "#0a1628",
      bgTo: "#02080e",
      accent: "#60a5fa",
      accentGlow: "rgba(96,165,250,0.35)",
      surface: "rgba(96,165,250,0.06)",
    },
  },

  disney: {
    id: "disney",
    name: "Disney",
    emoji: "✨",
    kidRole: "Hero",
    parentRole: "Fairy Godparent",
    coParentRole: "Enchanter",
    superParentRole: "Sorcerer",
    challengeName: "Adventure",
    challengeNamePlural: "Adventures",
    pointName: "Wish",
    pointNamePlural: "Wishes",
    pointEmoji: "⭐",
    difficulty: { common: "Tiny", rare: "Sparkling", epic: "Magical", legendary: "Legendary" },
    challengeLogTitle: "Adventure Book",
    homeTitle: "Your Castle",
    familyHubTitle: "Family Magic",
    pointBankTitle: "Wish Jar",
    visual: {
      bgFrom: "#1a0635",
      bgTo: "#08011a",
      accent: "#e879f9",
      accentGlow: "rgba(232,121,249,0.3)",
      surface: "rgba(232,121,249,0.06)",
    },
  },
};

export const DEFAULT_THEME_ID = "base";
